import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

import {
  FilterModel,
  FilterModels
} from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-filter.types';
import { MultiselectItem } from '@ui/aos-lib';
import { translationKeysMap } from 'src/app/services/translate/translate.key-mapper';
import {
  GetChartDataItem,
  GetChartDataPayload,
  GetChartDataResponse,
  GetFiltersPayload,
  GetFiltersResponse,
  GetTableDataItem,
  GetTableDataPayload,
  GetTableDataResponse,
  MeterConsumptionFieldName
} from './meter-consumption-tab-content.types';
import { TranslateService } from 'src/app/services/translate/translate.service';
import { MeasuringPeriod, UnitOfMeasurePeriod } from '@portal/shared/models/electrical-device-models';
import { ListResponse } from '@portal/shared/types/api-response.types';
import { DeviceHealthPresentation } from '../../device-details-metrics.types';
import { DeviceTimeSeries } from 'src/app/pages/equipment/health-monitor/health-api.types';
import { HttpClient } from '@angular/common/http';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { FlowDirection } from '@portal/shared/types/filter.types';

@Injectable({ providedIn: 'root' })
export class MeterConsumptionTabContentService {
  private readonly translate = inject(TranslateService);
  readonly #httpClient = inject(HttpClient);
  readonly #timeZoneService = inject(TimezoneService);

  getFilters(payload: GetFiltersPayload): Observable<FilterModels> {
    return this.#httpClient.post<GetFiltersResponse>('/api/device-electric-consumption-filters', payload).pipe(
      switchMap(response => {
        return of([
          this.#createFlowDirectionFilter(response.flowDirections ?? []),
          this.#createMeasuringPeriodFilter(response.measuringPeriods ?? []),
          this.#createUnitOfMeasureFilter(response.unitsOfMeasureWithMultipliers ?? [])
        ]);
      }),
      catchError(error => {
        console.error(error);
        return of([]);
      })
    );
  }

  getChartData(payload: GetChartDataPayload): Observable<DeviceHealthPresentation[]> {
    const uomFilter = payload.filters.find(f => f.filterType === MeterConsumptionFieldName.unitOfMeasure);
    const multiplierFilter = payload.filters.find(f => f.filterType === MeterConsumptionFieldName.multiplier);
    const rawUom = uomFilter?.filteredValues?.[0] ?? null;
    const multiplierValue = multiplierFilter?.filteredValues?.[0] ?? null;

    return this.#httpClient.post<GetChartDataResponse>('api/device-electric-consumption-chart', payload).pipe(
      map(response =>
        Object.entries(response).map(([key, value]: [string, GetChartDataItem[]]) => {
          return {
            type: key as keyof GetChartDataResponse,
            health: {
              data: value.map((item: GetChartDataItem): DeviceTimeSeries => ({ x: item.date, y: item.value })),
              type: key as keyof GetChartDataResponse,
              unitOfMeasure:
                multiplierValue && rawUom
                  ? `${multiplierValue}${rawUom.charAt(0).toUpperCase() + rawUom.slice(1)}`
                  : rawUom,
              multiplier: ''
            },
            config: []
          };
        })
      )
    );
  }

  getTableData(payload: GetTableDataPayload): Observable<ListResponse<GetTableDataItem>> {
    return this.#httpClient.post<GetTableDataResponse>('api/device-electric-consumption-list', payload).pipe(
      map(response => ({
        items: response.items.map(item => {
          return {
            ...item,
            date: item.date ? this.#timeZoneService.formatLong(item.date) : item.date
          };
        }),
        totalCount: response.totalCount
      }))
    );
  }

  #createFlowDirectionFilter(flowDirections: FlowDirection[]): FilterModel {
    try {
      const flowDirectionOrder = [FlowDirection.Forward, FlowDirection.Reverse, FlowDirection.Net, FlowDirection.Total];
      const availableItemsSet = new Set(flowDirections);
      const flowDirectionListOrdered = flowDirectionOrder.reduce(
        (acc: string[], curr) => (availableItemsSet.has(curr) ? [...acc, curr] : acc),
        []
      );
      const multiselectItems: MultiselectItem[] = flowDirectionListOrdered.map(item => ({
        text: translationKeysMap.get(item) || item,
        value: item
      }));
      const defaultSelectedItems = multiselectItems.filter(item =>
        [FlowDirection.Forward, FlowDirection.Reverse].includes(item.value as FlowDirection)
      );
      const selectedMultiselectItems = defaultSelectedItems.length
        ? defaultSelectedItems
        : multiselectItems.length
          ? [multiselectItems[0]]
          : [];

      return {
        filterType: MeterConsumptionFieldName.flowDirection,
        title: 'FlowDirection',
        isMultiTag: true,
        width: 187,
        items: this.translate.getTranslatedItems(multiselectItems),
        selected: this.translate.getTranslatedItems(selectedMultiselectItems)
      };
    } catch (e) {
      console.error(e);
      return {
        filterType: MeterConsumptionFieldName.flowDirection,
        title: 'FlowDirection',
        width: 187,
        items: [],
        selected: []
      };
    }
  }

  #createMeasuringPeriodFilter(measuringPeriods: string[]): FilterModel {
    try {
      const order = [
        MeasuringPeriod.twentyfourHour,
        MeasuringPeriod.sixtyMinute,
        MeasuringPeriod.thirtyMinute,
        MeasuringPeriod.fifteenMinute,
        MeasuringPeriod.fiveMinute,
        MeasuringPeriod.oneMinute
      ];
      const availableItemsSet = new Set(measuringPeriods);
      const orderedItems = order.reduce(
        (acc: string[], curr) => (availableItemsSet.has(curr) ? [...acc, curr] : acc),
        []
      );
      const multiselectItems: MultiselectItem[] = orderedItems.map(item => ({
        text: translationKeysMap.get(item) || item,
        value: item
      }));
      const selectedMultiselectItems = multiselectItems.length ? [multiselectItems[0]] : [];

      return {
        filterType: MeterConsumptionFieldName.measuringPeriod,
        title: 'MeasuringPeriod',
        width: 100,
        items: this.translate.getTranslatedItems(multiselectItems),
        selected: this.translate.getTranslatedItems(selectedMultiselectItems)
      };
    } catch (e) {
      console.error(e);
      return {
        filterType: MeterConsumptionFieldName.measuringPeriod,
        title: 'MeasuringPeriod',
        width: 100,
        items: [],
        selected: []
      };
    }
  }

  #createUnitOfMeasureFilter(
    unitsOfMeasureWithMultipliers: { multiplier: string; unitOfMeasure: string }[]
  ): FilterModel {
    try {
      const order = [UnitOfMeasurePeriod.wh, UnitOfMeasurePeriod.vAh, UnitOfMeasurePeriod.vArH, UnitOfMeasurePeriod.qh];
      const availableItemsMap: Map<string, { multiplier: string; unitOfMeasure: string }> = new Map(
        unitsOfMeasureWithMultipliers.map(u => [u.unitOfMeasure, u])
      );
      const orderedItems = order.reduce(
        (acc: { multiplier: string; unitOfMeasure: string }[], curr) =>
          availableItemsMap.has(curr) ? [...acc, availableItemsMap.get(curr)!] : acc,
        []
      );
      const multiselectItems: MultiselectItem[] = orderedItems.map(item => {
        const text = item.multiplier
          ? `${item.multiplier}${item.unitOfMeasure.charAt(0).toUpperCase()}${item.unitOfMeasure.slice(1)}`
          : item.unitOfMeasure;
        return {
          text: translationKeysMap.get(text) || text,
          value: text,
          multiplier: item.multiplier
        };
      });
      const selectedMultiselectItems = multiselectItems.length ? [multiselectItems[0]] : [];

      return {
        filterType: MeterConsumptionFieldName.unitOfMeasure,
        title: 'UnitOfMeasure',
        width: 100,
        items: this.translate.getTranslatedItems(multiselectItems),
        selected: this.translate.getTranslatedItems(selectedMultiselectItems)
      };
    } catch (e) {
      console.error(e);
      return {
        filterType: MeterConsumptionFieldName.unitOfMeasure,
        title: 'UnitOfMeasure',
        width: 100,
        items: [],
        selected: []
      };
    }
  }
}
